export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: number;
  level: LogLevel;
  module: string;
  message: string;
  stack?: string;
  data?: any;
}

export interface LogErrorNotification {
  module: string;
  message: string;
  error?: any;
  displayMessage: string;
  title: string;
}

export type LogErrorNotifier = (notification: LogErrorNotification) => void;

/**
 * LogService
 * 全局日志服务：负责日志记录、控制台输出和内存保存。
 *
 * UI 通知由 app 层注入，避免 shared 反向依赖 modules/ui。
 */
export class LogService {
  private static readonly MAX_LOGS = 100;
  private static logs: LogEntry[] = [];
  private static errorNotifier: LogErrorNotifier | null = null;

  /**
   * 初始化 LogService，可选绑定错误通知桥接。
   * 在 App 初始化并注册 Pinia 后调用。
   */
  static init(errorNotifier?: LogErrorNotifier): void {
    if (errorNotifier) {
      this.setErrorNotifier(errorNotifier);
    }
    this.info('LogService', '日志服务初始化完成');
  }

  /**
   * 设置错误通知回调，由 app/UI 层负责接入 Toast。
   */
  static setErrorNotifier(errorNotifier: LogErrorNotifier | null): void {
    this.errorNotifier = errorNotifier;
  }

  /**
   * 记录普通信息
   */
  static info(module: string, message: string, data?: any): void {
    this.addLog('info', module, message, undefined, data);
  }

  /**
   * 记录警告信息
   */
  static warn(module: string, message: string, data?: any): void {
    this.addLog('warn', module, message, undefined, data);
  }

  /**
   * 记录错误信息并触发注入的全局通知
   */
  static error(module: string, message: string, error?: any): void {
    const stack = error instanceof Error ? error.stack : undefined;
    this.addLog('error', module, message, stack, error);
    this.notifyError(module, message, error);
  }

  /**
   * 导出内存中的日志
   */
  static exportLogs(): string {
    return JSON.stringify(
      this.logs.map(log => ({
        ...log,
        time: new Date(log.timestamp).toISOString(),
      })),
      null,
      2,
    );
  }

  /**
   * 获取内存日志快照（按时间升序）
   */
  static getLogs(): LogEntry[] {
    return this.logs.map(log => ({
      ...log,
    }));
  }

  private static notifyError(module: string, message: string, error?: any): void {
    if (!this.errorNotifier) return;

    const displayMessage = message + (error instanceof Error ? `: ${error.message}` : '');
    try {
      this.errorNotifier({
        module,
        message,
        error,
        displayMessage,
        title: `${module} 模块错误`,
      });
    } catch (notifyError) {
      // 通知桥接失败不能影响日志本身。
      console.error('[LogService] 错误通知回调失败:', notifyError);
    }
  }

  /**
   * 内部添加日志方法
   */
  private static addLog(level: LogLevel, module: string, message: string, stack?: string, data?: any): void {
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      module,
      message,
      stack,
      data,
    };

    // 1. 存入内存队列
    this.logs.push(entry);
    if (this.logs.length > this.MAX_LOGS) {
      this.logs.shift();
    }

    // 2. 输出到控制台 (带样式)
    const timeStr = new Date(entry.timestamp).toLocaleTimeString();
    const prefix = `[${module}]`;
    const fullMessage = `${prefix} ${message}`;

    switch (level) {
      case 'info':
        console.log(`%c[${timeStr}] ${fullMessage}`, 'color: #4CAF50', data !== undefined ? data : '');
        break;
      case 'warn':
        console.warn(`%c[${timeStr}] ${fullMessage}`, 'color: #FFC107', data !== undefined ? data : '');
        break;
      case 'error':
        console.error(
          `%c[${timeStr}] ${fullMessage}`,
          'color: #F44336; font-weight: bold',
          data !== undefined ? data : '',
          stack || '',
        );
        break;
    }
  }
}
